import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { VatRateCell } from '@/components/table/cells/vat-rate-cell';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SelectPortalContainerContext } from '@/components/ui/select';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipPortalContainerContext,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  type DealItem,
  ValidationRuleId,
} from '@/lib/graphql/generated/graphql';
import { useApplyVatRate } from '@/lib/hooks/useApplyVatRate';
import { useEnsureDealItemDescriptions } from '@/lib/hooks/useEnsureDealItemDescriptions';
import { useToggleVatMargin } from '@/lib/hooks/useToggleVatMargin';
import { formatDecimal } from '@/lib/utils';
import {
  isDealItemCategoryUpdating,
  isDealItemVatRateUpdating,
} from '@/lib/utils/updating-fields';
import { parseVatCoherenceMessage } from '@/lib/utils/validation-error-translator';
import { isSameVatRate } from '@/lib/utils/vat-rates';
import { dealItemDescriptionsByActiveVersionIdState } from '@/states/deal-item-descriptions.state';
import { effectiveValidationErrorsAtom } from '@/states/effective-validation-errors.state';
import { productsMapState } from '@/states/products.state';
import { useAtomValue } from 'jotai';
import { CheckCircle2 } from 'lucide-react';

/**
 * Descriptions are stored as HTML; the review only needs the words. Same
 * normalization as the table's description preview tooltip.
 */
/**
 * Beyond this many characters the description no longer fits the clamped cell,
 * so it gets a hover tooltip carrying the full text. A character count rather
 * than a measured overflow: it costs no layout read, and being off by a line on
 * an edge case only means an extra (harmless) tooltip.
 */
const DESCRIPTION_CLAMP_CHARS = 110;

const toPlainText = (raw: string): string =>
  raw
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

interface VatCoherenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  /** Items flagged by the `vat_coherence` rule when the dialog was opened. */
  itemIds: string[];
}

/**
 * Review dialog for the `vat_coherence` anomaly: a restricted, read-only view of
 * the flagged items (product, declared VAT rate, LLM verdict) with the rate the
 * judge proposes, applied in one click.
 *
 * The judge is an LLM and can be wrong, so the point of the dialog is to show
 * its reasoning next to the data it judged and let the sale decide, rather than
 * to send the user filtering the main table with no explanation.
 */
export const VatCoherenceDialog: React.FC<VatCoherenceDialogProps> = ({
  open,
  onOpenChange,
  dealId,
  itemIds,
}) => {
  const { t } = useTranslation();
  // The rate picker and the tooltips portal into the dialog itself: portalled
  // into the body they land under the modal's overlay and inherit its
  // `pointer-events: none`, so they never open. Same fix as the import dialog.
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );
  const contentRefCallback = useCallback(
    (node: HTMLElement | null) => setPortalContainer(node),
    []
  );
  const productsMap = useAtomValue(productsMapState);
  const validationErrors = useAtomValue(effectiveValidationErrorsAtom);
  const isOfferLocked = useIsOfferLocked();
  const applyVatRate = useApplyVatRate(dealId);
  const toggleVatMargin = useToggleVatMargin(dealId);
  const descriptions = useAtomValue(dealItemDescriptionsByActiveVersionIdState);
  const ensureDescriptions = useEnsureDealItemDescriptions(dealId);
  // Last description seen for each reviewed item.
  //
  // Both sources go empty at the same moment: an items update replaces
  // `activeVersion` wholesale (the stream carries no description), and a signal
  // that creates items purges the shared cache. Without this the column would
  // blink to "—" for the length of a round trip, mid-review. `hasDescription`
  // still comes from the stream, so a description genuinely emptied elsewhere is
  // not masked by the memory.
  const lastKnownDescriptions = useRef(new Map<string, string>());

  // Frozen for the lifetime of the dialog, which is mounted only while open:
  // correcting a rate clears the backend error, and a row vanishing under
  // the cursor would lose the user's place mid-review. Rows that get fixed stay
  // listed and switch to a "resolved" state instead.
  const [reviewedItemIds] = useState<string[]>(itemIds);
  const [editingRateItemId, setEditingRateItemId] = useState<string | null>(
    null
  );

  // The description is what the judge read to reach its verdict, so the review
  // needs it — and the items list query leaves it out to stay light.
  //
  // Keyed on the cache itself, not just on `open`: a signal that creates
  // descriptions (an import, a resync) purges it back to `null` mid-review, and
  // the items stream carries no description, so the column would silently fall
  // back to "—" until the dialog is reopened.
  useEffect(() => {
    if (open && descriptions === null) void ensureDescriptions();
  }, [open, descriptions, ensureDescriptions]);

  /**
   * Description to display for a row:
   * - `null` — the product genuinely has none;
   * - `undefined` — it has one that is not loaded (or momentarily lost), so the
   *   cell waits instead of claiming there is none;
   * - a string — the text, remembered so a reload does not blank the cell.
   */
  const resolveDescription = useCallback(
    (itemId: string, activeVersion: DealItem): string | null | undefined => {
      const memory = lastKnownDescriptions.current;
      if (!activeVersion.hasDescription) {
        memory.delete(itemId);
        return null;
      }

      const text = toPlainText(
        activeVersion.description ?? descriptions?.[activeVersion.id] ?? ''
      );
      if (text) {
        memory.set(itemId, text);
        return text;
      }

      return memory.get(itemId);
    },
    [descriptions]
  );

  const rows = useMemo(
    () =>
      reviewedItemIds.flatMap((id) => {
        const product = productsMap.get(id);
        if (!product) return [];
        const error = validationErrors
          .get(id)
          ?.find((error) => error.ruleId === ValidationRuleId.VatCoherence);

        return [
          {
            id,
            activeVersion: product.activeVersion,
            verdict: error ? parseVatCoherenceMessage(error.message) : null,
            proposedVatRate:
              error?.proposedVatRate == null
                ? null
                : error.proposedVatRate * 100,
            isUpdating:
              isDealItemCategoryUpdating(product) ||
              isDealItemVatRateUpdating(product),
            description: resolveDescription(id, product.activeVersion),
          },
        ];
      }),
    [reviewedItemIds, productsMap, validationErrors, resolveDescription]
  );

  const hasAnyImage = rows.some(({ activeVersion }) =>
    Boolean(activeVersion.images[0])
  );
  const hasAnyDescription = rows.some(
    ({ activeVersion }) => activeVersion.hasDescription
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={contentRefCallback}
        className="flex max-h-[90vh] w-[95vw] flex-col gap-0 sm:max-w-[1400px]"
      >
        <SelectPortalContainerContext.Provider value={portalContainer}>
          <DialogHeader>
            <DialogTitle>
              {t('components.vat_coherence_dialog.title')}
            </DialogTitle>
            <DialogDescription>
              {t('components.vat_coherence_dialog.description')}
            </DialogDescription>
          </DialogHeader>

          {/* The shared cells render their tooltips with `skipTooltipProvider`,
            counting on the surface to provide one — the main table does it from
            `table-products.tsx`. Tooltips portal into the dialog: portalled
            into the body they land under the overlay. */}
          <TooltipProvider>
            <TooltipPortalContainerContext.Provider value={portalContainer}>
              {/* `Table` from ui/table wraps itself in an `overflow-auto` div,
                which would clip the sticky header against this scroller — hence
                the bare `<table>` with the same classes. The vertical margins
                live on the scroller, not inside it, so the gap above the first
                row does not scroll away. */}
              <div className="my-4 min-h-0 flex-1 overflow-y-auto border border-gray-200">
                <table className="w-full caption-bottom text-sm">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky top-0 z-20 w-[260px] border-b bg-white">
                        {t('kpi.name')}
                      </TableHead>
                      {hasAnyDescription && (
                        <TableHead className="sticky top-0 z-20 w-[320px] border-b bg-white">
                          {t('kpi.description')}
                        </TableHead>
                      )}
                      <TableHead className="sticky top-0 z-20 w-[100px] border-b bg-white">
                        {t('kpi.vat')}
                      </TableHead>
                      <TableHead className="sticky top-0 z-20 w-[220px] border-b bg-white">
                        {t('components.vat_coherence_dialog.column_proposal')}
                      </TableHead>
                      <TableHead className="sticky top-0 z-20 border-b bg-white">
                        {t('components.vat_coherence_dialog.column_verdict')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map(
                      ({
                        id,
                        activeVersion,
                        verdict,
                        proposedVatRate,
                        isUpdating,
                        description,
                      }) => (
                        <TableRow
                          key={id}
                          data-testid={`vat-coherence-row-${id}`}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {hasAnyImage &&
                                (activeVersion.images[0] ? (
                                  <img
                                    src={activeVersion.images[0]}
                                    alt=""
                                    className="size-10 shrink-0 rounded object-cover"
                                  />
                                ) : (
                                  <div className="size-10 shrink-0 rounded bg-gray-100" />
                                ))}
                              <span
                                className="truncate text-sm text-gray-900"
                                title={activeVersion.name}
                              >
                                {activeVersion.name || '—'}
                              </span>
                            </div>
                          </TableCell>
                          {hasAnyDescription && (
                            <TableCell>
                              {description == null ? (
                                <span className="text-sm text-gray-400">
                                  {description === null ? '—' : '…'}
                                </span>
                              ) : description.length >
                                DESCRIPTION_CLAMP_CHARS ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <p className="line-clamp-3 cursor-default text-sm text-gray-600">
                                      {description}
                                    </p>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="bottom"
                                    className="max-h-[50vh] max-w-md overflow-y-auto"
                                  >
                                    <p className="whitespace-pre-wrap break-words text-xs">
                                      {description}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <p className="text-sm text-gray-600">
                                  {description}
                                </p>
                              )}
                            </TableCell>
                          )}
                          <TableCell className="p-0">
                            <VatRateCell
                              dealItemId={id}
                              displayValue={activeVersion.vatRate}
                              hasVatMargin={activeVersion.hasVatMargin}
                              isMarginChecked={activeVersion.hasVatMargin}
                              isParent={false}
                              isOfferLocked={isOfferLocked}
                              isRecentlyModified={false}
                              onToggleVatMargin={toggleVatMargin}
                              showMarginToggle={activeVersion.hasVatMargin}
                              rateSelection={
                                activeVersion.hasVatMargin || isUpdating
                                  ? undefined
                                  : {
                                      isEditing: editingRateItemId === id,
                                      onOpen: () => setEditingRateItemId(id),
                                      onClose: () => setEditingRateItemId(null),
                                      onSelect: (rate) => {
                                        setEditingRateItemId(null);
                                        if (
                                          activeVersion.vatRate == null ||
                                          !isSameVatRate(
                                            activeVersion.vatRate,
                                            rate
                                          )
                                        ) {
                                          void applyVatRate(id, rate);
                                        }
                                      },
                                    }
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {verdict &&
                              proposedVatRate != null &&
                              (activeVersion.hasVatMargin ? (
                                <span className="text-sm text-gray-500">
                                  {t(
                                    'components.vat_coherence_dialog.margin_not_applicable'
                                  )}
                                </span>
                              ) : activeVersion.vatRate != null &&
                                isSameVatRate(
                                  activeVersion.vatRate,
                                  proposedVatRate
                                ) ? (
                                <span className="text-sm text-gray-500">
                                  {t('components.vat_coherence_dialog.applied')}
                                </span>
                              ) : (
                                <div className="flex items-center gap-3">
                                  <span className="text-sm text-gray-900">
                                    {t(
                                      'components.vat_coherence_dialog.proposal',
                                      {
                                        initial:
                                          activeVersion.vatRate == null
                                            ? '—'
                                            : `${formatDecimal(activeVersion.vatRate)}%`,
                                        proposed: `${formatDecimal(proposedVatRate)}%`,
                                      }
                                    )}
                                  </span>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    disabled={isOfferLocked || isUpdating}
                                    onClick={() =>
                                      void applyVatRate(id, proposedVatRate)
                                    }
                                  >
                                    {t('components.vat_coherence_dialog.fix')}
                                  </Button>
                                </div>
                              ))}
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {verdict ? (
                              <p>{verdict.reason}</p>
                            ) : (
                              <span className="flex items-center gap-1.5 text-green-600">
                                <CheckCircle2 className="size-4 shrink-0" />
                                {t('components.vat_coherence_dialog.resolved')}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={hasAnyDescription ? 5 : 4}
                          className="py-4 text-center text-gray-500"
                        >
                          {t('components.vat_coherence_dialog.empty')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </table>
              </div>
            </TooltipPortalContainerContext.Provider>
          </TooltipProvider>

          <DialogFooter className="shrink-0 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('components.vat_coherence_dialog.close')}
            </Button>
          </DialogFooter>
        </SelectPortalContainerContext.Provider>
      </DialogContent>
    </Dialog>
  );
};
